/**
 * CellDetailsPanel - Panel de detalles de una celda H3
 *
 * @version 1.0
 * @updated 2026-02-25
 * @module core/layouts/panels/RightPanel
 *
 * @description
 * Panel lateral que muestra información detallada de una celda:
 * - Código de la celda (no editable)
 * - Nombre personalizado de la celda (editable)
 * - Estadísticas de elementos dentro de la celda
 *
 * @remarks
 * Se muestra como un "subpanel" del GridCellsPanel con:
 * - Franja vertical izquierda con nombre de la malla
 * - Panel principal a la derecha con detalles de la celda
 */

import { useState, useEffect } from 'react';
import { X, TreeDeciduous, Trees, Umbrella, Droplets, Waves, Droplet } from 'lucide-react';
import type { CellRow } from '../../../spatial';
import type { SpatialGrid } from '../../../spatial';
import { useEditLock } from '../../EditLockContext';
import './cell-details-panel.css';

/**
 * Props del componente
 */
export interface CellDetailsPanelProps {
  /**
   * Malla activa (para mostrar nombre en franja lateral)
   */
  grid: SpatialGrid;

  /**
   * Datos de la celda seleccionada
   */
  cell: CellRow;

  /**
   * Modo operacional actual ('edit' | 'analyze')
   */
  operationalMode: 'edit' | 'analyze';

  /**
   * Callback para volver al listado de celdas
   */
  onBack: () => void;

  /**
   * Callback para cerrar el panel
   */
  onClose: () => void;

  /**
   * Callback para guardar el nombre de la celda
   */
  onSaveCellName: (h3Index: string, name: string) => Promise<void>;
}

/**
 * Iconos para cada tipo de elemento ambiental
 */
const ELEMENT_ICONS = {
  trees: <TreeDeciduous size={16} />,
  parks: <Trees size={16} />,
  sunshades: <Umbrella size={16} />,
  fountains: <Droplets size={16} />,
  waterCourses: <Waves size={16} />,
  waterMirrors: <Droplet size={16} />,
} as const;

/**
 * Labels para cada tipo de elemento
 */
const ELEMENT_LABELS = {
  trees: 'Árboles',
  parks: 'Parques, Plazas y Jardines',
  sunshades: 'Parasoles',
  fountains: 'Fuentes y Bebederos',
  waterCourses: 'Cursos de Agua',
  waterMirrors: 'Espejos de Agua',
} as const;

/**
 * Panel de detalles de celda
 */
export function CellDetailsPanel({
  grid,
  cell,
  operationalMode,
  onBack,
  onClose,
  onSaveCellName,
}: CellDetailsPanelProps): JSX.Element {
  const { startEditing, stopEditing, isEditingActive } = useEditLock();
  const [cellName, setCellName] = useState(cell.cellName ?? '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Detecta si hay cambios no guardados en el nombre
   */
  const hasUnsavedChanges = cellName !== (cell.cellName ?? '');

  /**
   * Activa bloqueo de edición cuando hay cambios y está en modo edición
   */
  useEffect(() => {
    if (hasUnsavedChanges && operationalMode === 'edit' && !isEditingActive) {
      startEditing('cell');
    }
  }, [hasUnsavedChanges, operationalMode, isEditingActive, startEditing]);

  /**
   * Desactiva bloqueo cuando cambia la celda
   */
  useEffect(() => {
    return () => {
      stopEditing();
    };
  }, [cell.h3Index, stopEditing]);

  /**
   * Guarda el nombre de la celda
   */
  const handleSaveName = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await onSaveCellName(cell.h3Index, cellName);
      setIsEditingName(false);

      // Desactivar bloqueo de edición
      stopEditing();
    } catch (error) {
      console.error('[CellDetailsPanel] Error guardando nombre:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Maneja el click en la barra lateral para volver al listado
   */
  const handleBackClick = (): void => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Hay cambios sin guardar en el nombre de la celda. ¿Desea descartarlos y volver al listado?'
      );
      if (!confirmed) return;
    }

    // Desactivar bloqueo de edición
    stopEditing();

    onBack();
  };

  /**
   * Calcula el total de elementos en la celda
   */
  const totalElements = Object.values(cell.env).reduce((sum, count) => sum + count, 0);

  return (
    <div className="cell-details-panel">
      {/* Franja vertical izquierda con nombre de la malla (clickeable para volver) */}
      <div
        className="cell-details-sidebar"
        onClick={handleBackClick}
        title="Volver al listado de celdas"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBackClick();
          }
        }}
      >
        <span className="cell-details-grid-name">{grid.name}</span>
      </div>

      {/* Panel principal de detalles */}
      <div className="cell-details-main">
        {/* Header con código de celda y botón de cierre */}
        <header className="cell-details-header">
          <div className="cell-details-header-content">
            <h3 className="cell-details-cell-code">{cell.cellCode ?? cell.h3Index}</h3>
            <button
              className="cell-details-close-btn"
              onClick={onClose}
              title="Cerrar detalles de celda"
              aria-label="Cerrar"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        {/* Contenido del panel */}
        <div className="cell-details-content">
          {/* Sección: Nombre de la celda */}
          <section className="cell-details-section">
            <h4 className="cell-details-section-title">Nombre de la Celda</h4>
            {isEditingName && operationalMode === 'edit' ? (
              <div className="cell-name-edit-group">
                <input
                  type="text"
                  className="cell-name-input"
                  value={cellName}
                  onChange={(e) => setCellName(e.target.value)}
                  placeholder="Ingrese un nombre..."
                  maxLength={100}
                  disabled={isSaving}
                  autoFocus
                />
                <div className="cell-name-actions">
                  <button
                    className="cell-name-btn cell-name-btn--save"
                    onClick={handleSaveName}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Guardando...' : 'Guardar'}
                  </button>
                  <button
                    className="cell-name-btn cell-name-btn--cancel"
                    onClick={() => {
                      setCellName(cell.cellName ?? '');
                      setIsEditingName(false);
                    }}
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`cell-name-display ${operationalMode === 'edit' ? 'cell-name-display--editable' : ''}`}
                onClick={() => {
                  if (operationalMode === 'edit') {
                    setIsEditingName(true);
                  }
                }}
                style={{ cursor: operationalMode === 'edit' ? 'pointer' : 'default' }}
              >
                {cell.cellName ? (
                  <span className="cell-name-text">{cell.cellName}</span>
                ) : operationalMode === 'edit' ? (
                  <span className="cell-name-placeholder">+ Agregar nombre</span>
                ) : (
                  <span className="cell-name-text" style={{ opacity: 0.5 }}>Sin nombre</span>
                )}
              </div>
            )}
          </section>

          {/* Sección: Estadísticas de elementos */}
          <section className="cell-details-section">
            <h4 className="cell-details-section-title">
              Elementos en la Celda
              <span className="cell-details-total-badge">{totalElements}</span>
            </h4>

            {totalElements === 0 ? (
              <p className="cell-details-empty">No hay elementos en esta celda</p>
            ) : (
              <div className="cell-stats-list">
                {Object.entries(cell.env).map(([key, count]) => {
                  // Solo mostrar elementos con conteo > 0
                  if (count === 0) return null;

                  const elementKey = key as keyof typeof ELEMENT_ICONS;
                  return (
                    <div key={key} className="cell-stat-item">
                      <div className="cell-stat-icon">{ELEMENT_ICONS[elementKey]}</div>
                      <div className="cell-stat-info">
                        <span className="cell-stat-label">{ELEMENT_LABELS[elementKey]}</span>
                        <span className="cell-stat-count">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
